import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import '../custom.css';

const BillView = () => {
  const { id } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [outlet, setOutlet] = useState(null);
  const [settlementStatus, setSettlementStatus] = useState(null);
  const [payingAmount, setPayingAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentModes, setPaymentModes] = useState([]);
  const [settlementDate, setSettlementDate] = useState(new Date().toISOString().slice(0, 10));
  const [balanceAmount, setBalanceAmount] = useState(0);
  const [formType, setFormType] = useState('settlement');
  const [cancelReason, setCancelReason] = useState('');
  const [otherRemark, setOtherRemark] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);

  const toNumber = (v) => {
    const n = Number(v);
    return isFinite(n) ? n : 0;
  };

  useEffect(() => {
      axios
          .get(`https://treatway.co.in/Counter-Backend/SalesApi/getOrderById/${id}`)
          .then((res) => {
              const parsedData = res.data.final_output ? JSON.parse(res.data.final_output) : res.data;

              setOrder(parsedData.header);
              setItems(parsedData.items);
              setOutlet(parsedData.outlet);
              setSettlementStatus(parsedData.settlement_status || 'PENDING');

              const backendBalance = parseFloat(parsedData.header?.balance_amount ?? parsedData.header?.balance_amt ?? 0);
              setBalanceAmount(isNaN(backendBalance) ? 0 : backendBalance);
              setPayingAmount('');
          })
          .catch((err) => {
              console.error('API Fetch Error:', err);
              toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load bill data.', life: 3000 });
          });
  }, [id]);

  const getTotal = () => {
      let cgst = 0;
      let sgst = 0;
      let taxable = 0;
      let grand = 0;

      items.forEach((i) => {
          const itemTaxableAmt = parseFloat(i.item_taxable_amt) || 0;
          const itemTotal = parseFloat(i.total_price) || 0;
          const cgstRate = parseFloat(i.cgst_tax) || 0;
          const sgstRate = parseFloat(i.sgst_tax) || 0;

          const cgstAmt = (itemTaxableAmt * cgstRate) / 100;
          const sgstAmt = (itemTaxableAmt * sgstRate) / 100;

          cgst += cgstAmt;
          sgst += sgstAmt;
          taxable += itemTaxableAmt;
          grand += itemTotal;
      });

      return { cgst, sgst, taxable, grand };
  };

  const getGroupedTax = () => {
      const taxGroups = {};
      items.forEach((i) => {
          const cgstRate = parseFloat(i.cgst_tax) || 0;
          const sgstRate = parseFloat(i.sgst_tax) || 0;
          const taxableAmt = parseFloat(i.item_taxable_amt) || 0;

          if (!taxGroups[`CGST@${cgstRate}`]) taxGroups[`CGST@${cgstRate}`] = 0;
          taxGroups[`CGST@${cgstRate}`] += (taxableAmt * cgstRate) / 100;

          if (!taxGroups[`SGST@${sgstRate}`]) taxGroups[`SGST@${sgstRate}`] = 0;
          taxGroups[`SGST@${sgstRate}`] += (taxableAmt * sgstRate) / 100;
      });

      return taxGroups;
  };

  useEffect(() => {
      axios
          .get(`https://treatway.co.in/Counter-Backend/SalesApi/getPaymentModes`)
          .then((res) => {
              const modes = res.data.map((mode) => ({
                  label: mode.setting_title,
                  value: mode.setting_title
              }));
              setPaymentModes(modes);
          })
          .catch((err) => {
              console.error('Failed to fetch payment modes:', err);
              toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load payment modes.', life: 3000 });
          });
  }, []);

  const numericBalance = toNumber(balanceAmount);
  const numericPaying = payingAmount === '' ? 0 : toNumber(payingAmount);
  const remainingPreview = Math.max(0, numericBalance - numericPaying);

  const handleSubmitSettlement = () => {
      const paying = toNumber(payingAmount);

      if (paying <= 0 || !paymentMode || !settlementDate) {
          toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Please fill all settlement fields with a valid amount.', life: 3000 });
          return;
      }

      if (paying > numericBalance) {
          toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Paying amount cannot exceed balance amount.', life: 3000 });
          return;
      }

      axios
          .post(
              `https://treatway.co.in/Counter-Backend/SalesApi/submitSettlement`,
              {
                  bill_id: id,
                  total_amount: parseFloat(getTotal().grand.toFixed(2)),
                  paying_amount: paying,
                  payment_mode: paymentMode,
                  settlement_date: settlementDate
              },
              { headers: { 'Content-Type': 'application/json' } }
          )
          .then((res) => {
              if (res.data.status === 'success') {
                  toast.current.show({ severity: 'success', summary: 'Success', detail: 'Settlement submitted successfully!', life: 3000 });
                  const newBal = parseFloat(res.data.new_balance_amount ?? res.data.balance_amount ?? remainingPreview);
                  setBalanceAmount(isNaN(newBal) ? remainingPreview : newBal);
                  setPayingAmount('');
                  setSettlementStatus(newBal <= 0 ? 'SETTLED' : 'PENDING');
                  setTimeout(() => {
                      navigate('/billreport');
                  }, 1500);
              } else {
                  toast.current.show({ severity: 'error', summary: 'Error', detail: res.data.message || 'Settlement failed.', life: 3000 });
              }
          })
          .catch((err) => {
              console.error('Settlement Error:', err);
              toast.current.show({ severity: 'error', summary: 'Error', detail: 'Settlement request failed.', life: 3000 });
          });
  };

  const confirmSettlement = () => {
      confirmDialog({
          message: 'Are you sure you want to submit this settlement?',
          header: 'Confirm Settlement',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Yes',
          rejectLabel: 'No',
          accept: handleSubmitSettlement
      });
  };

  const handleCancelBill = () => {
      const finalRemark = cancelReason === 'Others' ? otherRemark : cancelReason;

      axios
          .post(
              `https://treatway.co.in/Counter-Backend/SalesApi/cancelBill`,
              {
                  bill_id: id,
                  cancel_remark: finalRemark
              },
              { headers: { 'Content-Type': 'application/json' } }
          )
          .then((res) => {
              if (res.data.status === 'success') {
                  toast.current.show({ severity: 'success', summary: 'Success', detail: 'Bill cancelled successfully!', life: 3000 });
                  setSettlementStatus('CANCELLED');
                  setTimeout(() => {
                      navigate('/billreport');
                  }, 1500);
              } else {
                  toast.current.show({ severity: 'error', summary: 'Error', detail: res.data.message || 'Bill cancellation failed.', life: 3000 });
              }
          })
          .catch((err) => {
              console.error('Cancellation Error:', err);
              toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error cancelling bill.', life: 3000 });
          });
  };

  const confirmCancelBill = () => {
      if (!cancelReason) {
          toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Please select a cancel reason.', life: 3000 });
          return;
      }

      if (cancelReason === 'Others' && !otherRemark.trim()) {
          toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Please provide remark for "Others".', life: 3000 });
          return;
      }

      confirmDialog({
          message: 'Are you sure you want to cancel this bill?',
          header: 'Confirm Cancellation',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Yes',
          rejectLabel: 'No',
          accept: handleCancelBill
      });
  };

  const totals = getTotal();

  if (!order) return <div>Loading...</div>;

  return (
      <>
          <Toast ref={toast} />
          <ConfirmDialog />
          <div style={{ display: 'flex', gap: '30px', padding: '20px' }}>
              <div id="printable" style={{ width: '65%', fontFamily: 'Arial', background: '#fff', color: '#000' }}>
                  {/* Header */}
                  <div className="centered-header" style={{ textAlign: 'center' }}>
                      <h4>Tax Invoice</h4>
                      <h4>{outlet?.branch_title || 'Outlet Name'}</h4>
                      <p>{outlet?.address1}</p>
                      <p>{outlet?.address2}</p>
                      <p>
                          <b>GSTIN:</b> {outlet?.gstin_no}
                      </p>
                      <p>CIN NO : {outlet?.cin_no}, FSSAI : 000</p>
                      <p
                          style={{
                              color: order.status === 'closed' ? 'green' : order.status === 'cancelled' ? 'red' : 'orange',
                              fontWeight: 'bold',
                              fontSize: '14px'
                          }}
                      >
                          {order.status === 'closed' ? 'SETTLED' : order.status === 'cancelled' ? 'CANCELLED' : 'DISPATCHED'}
                      </p>
                  </div>

                  <hr style={{ marginTop: '2px', height: '2px', backgroundColor: '#000', border: 'none' }} />

                  {/* Order Details */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ textAlign: 'left' }}>
                          <p>
                              <b>Invoice No:</b> {order.bill_no}
                          </p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                          <p>
                              <b>Invoice Date:</b> {order.delivery_date}
                          </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                          <p>
                              <b>Invoice Time:</b> {order.delivery_time}
                          </p>
                      </div>
                  </div>

                  <hr style={{ marginTop: '2px', height: '2px', backgroundColor: '#000', border: 'none' }} />

                  {/* Items Table */}
                  <table width="100%" cellPadding="5" style={{ borderCollapse: 'collapse' }}>
                      <thead>
                          <tr style={{ borderBottom: '1px solid black' }}>
                              <th style={{ textAlign: 'left' }}>Item Name</th>
                              <th style={{ textAlign: 'right' }}>PRICE</th>
                              <th style={{ textAlign: 'right' }}>QTY</th>
                              <th style={{ textAlign: 'right' }}>VALUE</th>
                          </tr>
                      </thead>
                      <tbody>
                          {items.map((item, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #ccc' }}>
                                  <td>
                                      {item.product_name}
                                      <br />
                                      HSN Code : {item.hsn_code}
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                      {parseFloat(item.basic_rate).toFixed(2)}
                                      <br />
                                      CGST : {item.cgst_tax}%
                                  </td>
                                  <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                                  <td style={{ textAlign: 'right' }}>
                                      {parseFloat(item.item_taxable_amt).toFixed(2)}
                                      <br />
                                      SGST : {item.sgst_tax}%
                                  </td>
                              </tr>
                          ))}
                      </tbody>
                  </table>

                  {/* Totals */}
                  <div style={{ marginTop: '20px', textAlign: 'right' }}>
                      <p>
                          <b>Taxable Amount:</b> {totals.taxable.toFixed(2)}
                      </p>
                      {Object.entries(getGroupedTax()).map(([label, value], idx) => (
                          <p key={idx}>
                              <b>{label.replace('@', ' @ ')}%:</b> {value.toFixed(2)}
                          </p>
                      ))}
                      <hr style={{ width: '200px', float: 'right', margin: '5px 0' }} />
                      <p>
                          <b>Grand Total:</b> {totals.grand.toFixed(0)}
                      </p>
                  </div>

                  <div style={{ textAlign: 'center', marginTop: '30px' }}>
                      <p style={{ fontSize: '13px' }}>
                          <b>Computer generated Invoice, hence signature not required</b>
                      </p>
                  </div>

                  <div className="no-print" style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '20px' }}>
                      <Button label="Back" icon="pi pi-arrow-left" className="p-button-secondary" onClick={() => navigate(-1)} />
                      <Button label="Print" icon="pi pi-print" className="p-button-help" onClick={() => window.print()} />
                  </div>
              </div>

              {/* Toggle Form Section */}
              <div style={{ width: '35%', background: '#f9f9f9', padding: '20px', borderRadius: '8px', boxShadow: '0 0 10px #ccc' }}>
                  {order.status === 'pending' && (
                      <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                              <Button label="Settlement" onClick={() => setFormType('settlement')} className={`p-button ${formType === 'settlement' ? 'p-button-success' : 'p-button-outlined'}`} />
                              <Button label="Cancel Bill" onClick={() => setFormType('cancel')} className={`p-button ${formType === 'cancel' ? 'p-button-danger' : 'p-button-outlined'}`} />
                          </div>

                          {formType === 'settlement' && (
                              <>
                                  <h3>Settlement</h3>

                                  <div style={{ marginBottom: '15px' }}>
                                      <label>Balance Amount</label>
                                      <InputText value={numericBalance.toFixed(2)} readOnly style={{ width: '100%' }} />
                                  </div>

                                  <div style={{ marginBottom: '15px' }}>
                                      <label>Pay Amount</label>
                                      <InputText
                                          value={payingAmount}
                                          onChange={(e) => {
                                              const val = e.target.value;
                                              if (val === '') {
                                                  setPayingAmount('');
                                                  return;
                                              }
                                              const cleaned = val.replace(/[^0-9.]/g, '');
                                              if (cleaned.split('.').length > 2) return;
                                              if (cleaned === '.') {
                                                  setPayingAmount(cleaned);
                                                  return;
                                              }
                                              const num = parseFloat(cleaned);
                                              if (isNaN(num)) return;
                                              if (num <= numericBalance) {
                                                  setPayingAmount(cleaned);
                                              } else {
                                                  setPayingAmount(numericBalance.toFixed(2));
                                              }
                                          }}
                                          placeholder="Enter amount"
                                          style={{ width: '100%' }}
                                      />
                                  </div>

                                  <div style={{ marginBottom: '15px' }}>
                                      <label>Remaining Balance</label>
                                      <InputText value={remainingPreview.toFixed(2)} readOnly style={{ width: '100%' }} />
                                  </div>

                                  <div style={{ marginBottom: '15px' }}>
                                      <label>Payment Mode</label>
                                      <Dropdown value={paymentMode} onChange={(e) => setPaymentMode(e.value)} options={paymentModes} placeholder="Select Mode" style={{ width: '100%' }} />
                                  </div>

                                  <div style={{ marginBottom: '15px' }}>
                                      <label>Settlement Date</label>
                                      <Calendar value={new Date(settlementDate)} onChange={(e) => setSettlementDate(e.value.toISOString().slice(0, 10))} dateFormat="yy-mm-dd" showIcon style={{ width: '100%' }} />
                                  </div>

                                  <Button label="Submit Settlement" icon="pi pi-check" onClick={confirmSettlement} className="p-button-success" style={{ width: '100%' }} />
                              </>
                          )}

                          {formType === 'cancel' && (
                              <>
                                  <h3>Cancel Bill</h3>

                                  <div style={{ marginBottom: '15px' }}>
                                      <label>Cancel Remark</label>
                                      <Dropdown
                                          value={cancelReason}
                                          onChange={(e) => setCancelReason(e.value)}
                                          options={[
                                              { label: 'Item Missing', value: 'Item Missing' },
                                              { label: 'Delete Extra Items', value: 'Delete Extra Items' },
                                              { label: 'Others', value: 'Others' }
                                          ]}
                                          placeholder="Select Reason"
                                          style={{ width: '100%' }}
                                      />
                                  </div>

                                  {cancelReason === 'Others' && (
                                      <div style={{ marginBottom: '15px' }}>
                                          <label>Remarks</label>
                                          <InputText value={otherRemark} onChange={(e) => setOtherRemark(e.target.value)} placeholder="Enter details" style={{ width: '100%' }} />
                                      </div>
                                  )}

                                  <Button label="Cancel Bill" icon="pi pi-times" className="p-button-danger" onClick={confirmCancelBill} style={{ width: '100%' }} />
                              </>
                          )}
                      </>
                  )}

                  {/* Receipt Image */}
                  <div style={{ marginTop: '30px', textAlign: 'center' }}>
                      <Button label={showReceipt ? 'Hide Receipt Image' : 'Show Receipt Image'} onClick={() => setShowReceipt((prev) => !prev)} className="p-button-secondary" />

                      {showReceipt && order?.receipt_image && (
                          <div style={{ marginTop: '20px' }}>
                              <img
                                  src={`https://treatway.co.in/Counter-Backend/uploads/advanceorder_photo/${order.receipt_image}`}
                                  alt="Receipt"
                                  style={{
                                      width: '100%',
                                      maxHeight: '300px',
                                      objectFit: 'contain',
                                      border: '1px solid #ccc',
                                      padding: '5px',
                                      borderRadius: '4px',
                                      background: '#fff',
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                  }}
                              />
                          </div>
                      )}

                      {showReceipt && !order?.receipt_image && <p style={{ color: 'red', marginTop: '10px' }}>No receipt image available.</p>}
                  </div>
              </div>
          </div>
      </>
  );
};

export default BillView;
